import { Flex, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import type { DataTable } from "@/types";
import { htmlify, max, min } from "@/services/utils";
import { HypotheticalValue } from "../HypertheticalValue/HypotheticalValue";
import { PercentageBar } from "../PercentageBar/PercentageBar";
import { useMemo, useState } from "react";
import { transparentize } from "@chakra-ui/theme-tools";
import { PaginationControl } from "../PaginationControl/PaginationControl";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";
import { AllocationBreakdownAxisRow } from "../AllocationBreakdown/AllocationBreakdownAxisRow";

interface AllocationPerfomanceTableProps {
	data: DataTable;
	pageSize: number;
	translations: ShareClassTranslationMap
}

export const AllocationPerfomanceTable = ({ data, pageSize = 5, translations }: AllocationPerfomanceTableProps) => {
	const [currentPage, setCurrentPage] = useState(1);
	const minValue = useMemo(() => {
		return min(
			(data.rowData || []).map(({ weighting }) => {
				return weighting as number;
			})
		);
	}, [data]);
	const maxValue = useMemo(() => {
		return max(
			(data.rowData || []).map(({ weighting }) => {
				return weighting as number;
			})
		);
	}, [data]);
	const minScale = useMemo(() => {
		if (minValue < 0 || maxValue < 0) {
			return -Math.abs(Math.max(Math.abs(minValue), Math.abs(maxValue)));
		}
		return Math.min(0, minValue);
	}, [minValue, maxValue]);
	const maxScale = useMemo(() => {
		if (minScale < 0) {
			return -minScale;
		}
		return maxValue;
	}, [minScale, maxValue]);
	const differenceScale = (maxScale - minScale) * (minScale >= 0 ? 2 : 1);

	const pagination = useMemo(() => {
		return {
			pageIndex: currentPage - 1,
			pageCount: Math.ceil(data.rows.length / pageSize),
			totalItems: data.rows.length,
			pageSize
		};
	}, [currentPage, data, pageSize]);

	const visibleRows = useMemo(() => {
		const start = pagination.pageIndex * pageSize;
		return data.rows.slice(start, Math.min(start + pageSize, data.rows.length));
	}, [data, pagination, pageSize]);

	return (
		<TableContainer data-testid="PerfomanceTable">
			<Table
				variant="performances"
				overflow="auto"
				sx={{
					"tr:nth-of-type(odd)": {
						md: {
							bgColor: transparentize("brand.primary", 0.05)
						}
					},
				}}
			>
				{data.header && (
					<Thead>
						<Tr>
							{data.header.map((value, index) => {
								return (
									<Th
										scope="col"
										key={index}
										paddingX={2}
										paddingY={0.5}
										wordBreak="break-word"
										_first={{
											width: { md: "15%", xl: FIRST_COLUMN_WIDTH },
											paddingLeft: 2,
											textTransform: "math-auto",
											textAlign: "start",
											...mobileAfter,
											...mobileProps
										}}
									>
										<Text
											variant="digitRegular"
											fontSize="md"
											lineHeight={3}
											overflowWrap="anywhere"
											whiteSpace="break-spaces"
										>
											{htmlify(value)}
										</Text>
									</Th>
								);
							})}
							<Th
								paddingX={0.5}
								paddingY={0.5}
							>
								<AllocationBreakdownAxisRow min={Math.round(minScale)} max={Math.round(maxScale)} steps={2} width="100%" />
							</Th>
						</Tr>
					</Thead>
				)}
				<Tbody>
					{visibleRows.map((row, index) => {
						const rowIndex = (pagination.pageIndex * pagination.pageSize) + index;
						const rowData = (data.rowData?.[rowIndex] || {}) as { weighting: number; };

						return (
							<Tr key={index} data-testid={data.rowTypes[index]}>
								{row.map((value, index) => {
									return (
										<Td
											key={index}
											paddingX={2}
											paddingY={2}
											background={{
												md: "none !important"
											}}
											_first={{
												paddingLeft: 2,
												background: "inherit",
												...mobileAfter,
												...mobileProps,
												"& > div": {
													justifyContent: "start",
													width: { base: "120px", md: "unset" }
												}
											}}
											_notFirst={{
												"& > div": {
													width: { base: "120px", md: "unset" }
												}
											}}
										>
											<HypotheticalValue
												value={value as string}
												includeInvestmentAmount={false}
												format={data.cellFormatter}
											/>
										</Td>
									);
								})}
								<Td
									paddingX={1}
									paddingY={2}
									minWidth="200px"
								>
									<Flex direction="row" height="24px">
										{minScale < 0 && <PercentageBar
											flex={1}
											barDirection="right"
											barRanges={rowData.weighting < 0 ? [
												{
													min: 0,
													max: (Math.abs(rowData.weighting) / (differenceScale / 2)) * 100,
													color: "brand.primary"
												}
											] : undefined}
										/>}
										<PercentageBar
											flex={1}
											barDirection="left"
											barRanges={rowData.weighting > 0 ? [
												{
													min: 0,
													max: (Math.abs(rowData.weighting) / (differenceScale / 2)) * 100,
													color: "brand.primary"
												}
											] : undefined}
										/>
									</Flex>
								</Td>
							</Tr>
						);
					})}
				</Tbody>
			</Table>

			{pagination && <PaginationControl
				translations={translations}
				currentPage={pagination.pageIndex + 1}
				pageCount={pagination.pageCount}
				totalItems={pagination.totalItems}
				pageSize={pagination.pageSize}
				maxButtons={5}
				size="sm"
				py={4}
				onPageChange={(page: number) => {
					setCurrentPage(page);
				}}
			/>}
		</TableContainer>
	);
};

const FIRST_COLUMN_WIDTH = "20%";

const mobileProps = {
	position: { base: "sticky", md: "unset" },
	left: { base: "0", md: "unset" },
	zIndex: { base: "docked", md: "unset" },
	whiteSpace: { base: "break-spaces", md: "normal" }
};

const mobileAfter = {
	_after: {
		boxShadow: "inset 10px 0 8px -8px rgb(0 0 0 / 25%)",
		content: { base: `''`, md: "unset" },
		position: "absolute",
		top: 0,
		right: 0,
		bottom: "-1px",
		width: "30px",
		transform: "translateX(100%)"
	}
};
